import { useSyncExternalStore } from 'react';
import type { AppDisplayMode } from '@archipro-design/aria';

/**
 * Written to the root element by the inline scale script in `RootPage`, before
 * first paint and again whenever the window resizes or the pixel ratio changes.
 */
const ATTRIBUTE = 'data-ap-display';

const read = (): AppDisplayMode | null => {
    if (typeof document === 'undefined') return null;
    const value = document.documentElement.getAttribute(ATTRIBUTE);
    return value === 'mobile' || value === 'desktop' ? value : null;
};

/** Cached so `getSnapshot` stays a plain read; React calls it on every render. */
let current: AppDisplayMode | null = null;

/**
 * One observer for the whole document, fanning out to every subscriber.
 *
 * Giving each subscriber its own observer looks equivalent and is not: they
 * share the cache above, so whichever fired first would update it and leave the
 * rest comparing against a value that had already moved. Only that first
 * subscriber re-rendered. The page half-swapped — the header kept its desktop
 * layout in a phone-width viewport while the rest of the tree changed under it.
 */
const listeners = new Set<() => void>();
let observer: MutationObserver | null = null;

const subscribe = (onChange: () => void): (() => void) => {
    listeners.add(onChange);

    if (!observer) {
        current = read();
        observer = new MutationObserver(() => {
            const next = read();
            if (next === current) return;
            current = next;
            for (const listener of listeners) listener();
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: [ATTRIBUTE],
        });
    }

    return () => {
        listeners.delete(onChange);
        if (listeners.size === 0) {
            observer?.disconnect();
            observer = null;
        }
    };
};

/**
 * Which tree to render, decided by the room actually available rather than by
 * what kind of device asked for the page.
 *
 * The decision itself lives in the scale script, which owns it because the root
 * font-size rules key off the same attribute — one answer, read by both the CSS
 * and the components, rather than two that can drift apart. This hook only
 * watches for it changing.
 *
 * `serverMode` is the user-agent answer the server rendered with. It is the
 * server snapshot and the fallback, so first paint matches the HTML and a reader
 * whose script did not run keeps today's behaviour. Where the two disagree — a
 * zoomed desktop browser — the tree swaps once after hydration.
 */
export const useEffectiveDisplayMode = (
    serverMode: AppDisplayMode,
): AppDisplayMode =>
    useSyncExternalStore(
        subscribe,
        () => current ?? serverMode,
        () => serverMode,
    );
